import { Alert, Box, Button, Checkbox, FormControlLabel, Modal, TextField, Typography } from "@mui/material";
import { ApplicationItem, confirmMetadataRetrieval } from "../services/applicationsApi";
import Link from '@mui/material/Link';
import './AppDetails.css';
import React, { SetStateAction } from "react";
import { useMsal } from "@azure/msal-react";

interface AppDetailsEntraInfoProps {
    model: ApplicationItem | null;
    isViewMode: boolean;
    setModelOwnerConsentDate: React.Dispatch<SetStateAction<Date>>;
    modelOwnerConsentDate: Date | null;
    handleElementUpdate: (updatedApp: ApplicationItem, closeModals: boolean) => Promise<void>;
}

const AppDetailsEntraInfo = ({ model, isViewMode, setModelOwnerConsentDate, modelOwnerConsentDate, handleElementUpdate }: AppDetailsEntraInfoProps) => {
    const { instance } = useMsal();
    const slotProps = { inputLabel: { shrink: true } };
    const [isConfirmModalVisible, setIsConfirmModalVisible] = React.useState<boolean>(false);
    const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);
    const [isAdminWarningVisible, setIsAdminWarningVisible] = React.useState<boolean>(false);
    const isEntraMetadataSectionVisible = (status: string | null, appId: string | null): boolean => {
        if (isViewMode !== true) { return false; }
        if (status == null) { return false; }
        if (status == "Not Ready") { return false; }
        if (status == "Awaiting Initiation") { return false; }
        if (status == "Processing") { return false; }
        if (status == "Ready") { return true; }
        if (status == "AuthN Detected") { return true; }
        if (status == "Confirm Cutover") { return true; }
        if (status == "Complete") { return true; }
        if (status == "To Be Decommissioned") { return !!appId; }
        if (status == "Decommissioned") { return !!appId; }
        if (status == "Failed") { return false; }
        return false;
    };
    const isEntraMetadataAppVisible = (ownerConsentGiven: boolean | null): boolean => {
        if (ownerConsentGiven === true) { return true; }
        return false;
    };
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '840px',
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };
    const onSaveClick = async () => {
        await confirmMetadataRetrieval(instance, model.appInstanceId);
        await setModelOwnerConsentDate(new Date());
        await setIsConfirmModalVisible(false);
        model.ownerConsentDate = new Date();
        await handleElementUpdate(model, false);
    };

    return (
        isEntraMetadataSectionVisible(model?.originalStatus, model?.entraAppId)
            ? <div>
                <br />
                <Typography className="details-apps-group">Entra ID data</Typography>
                <Typography className="details-apps-title">The metadata for your migrated application in Entra ID</Typography>
                {isEntraMetadataAppVisible(modelOwnerConsentDate != null)
                    ? <div>
                        {model?.entraAppId == null ? '' : model.authenticationMethod != null && model.authenticationMethod.toLowerCase() === "oauth2"
                            ? <div>
                                <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_entraAppId" label="Client ID" defaultValue={model?.entraAppId} disabled={true} />
                                <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_entraAppTenantId" label="Tenant ID" defaultValue={model?.entraAppTenantId} disabled={true} />
                                <br /><Link target="_blank" href="https://pwc.sharepoint.com/:w:/t/GBL-SD-0AETbkQW9ccKBUk9PVA/EWr08IF9KxNFpomf4kHg-xwBWIAphcfjfRSA2JMDppbe5Q?e=yLjO89" underline="always">Entra ID Client Secrets & Certificates Guide</Link>
                            </div>
                            : <div>
                                <div>
                                    <Link target="_blank" href={"https://login.microsoftonline.com/" + model?.entraAppTenantId + "/federationmetadata/2007-06/federationmetadata.xml?appid=" + model?.entraAppId} underline="always">Link to Entra ID metadata</Link>
                                </div>
                                <div>
                                    <Link
                                        target="_blank"
                                        href={"https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/Overview/appId/" + model?.entraAppId}
                                        underline="always"
                                        onClick={() => setIsAdminWarningVisible(true)}
                                    >
                                        Link to Azure Entra Page
                                    </Link>
                                </div>
                            </div>

                        }
                    </div>
                    : <div><Button style={{ margin: '10px', float: 'left', width: '250px', backgroundColor: '#FD5107', textTransform: 'none' }} variant="contained" color="success" onClick={() => setIsConfirmModalVisible(true)}>Click to retrieve metadata</Button><br /><br /></div>
                }
                <Modal
                    open={isConfirmModalVisible}
                    onClose={() => setIsConfirmModalVisible(false)}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description">
                    <Box sx={modalStyle}>
                        <Alert severity="warning">Important Notice</Alert><br/>
                        <Typography className="details-apps-subtitle">
                            You are about to gather metadata for an application registered in Microsoft Entra ID.<br/><br/>
                            Only the <b>Application Owner</b> or a <b>delegated representative with explicit permission</b> may initiate the application migration process.<br/><br/>
                            By proceeding, you confirm that:<br />
                            <ul style={{ marginLeft: '20px' }}>
                                <li>You have reviewed this application's configuration.</li>
                                <li>You are authorised by the application owner to perform the migration.</li>
                                <li>You understand that the application owner is accountable for the outcome of this migration.</li>
                            </ul>
                        </Typography>
                        <br />
                        <FormControlLabel control={<Checkbox checked={isConfirmed} onChange={e => setIsConfirmed(e.target.checked) } />} label="I confirm that I have the required permissions to proceed on behalf of the application owner." />
                        <br /><br />
                        <div>
                            <Button style={{ margin: '10px', float: 'right', width: '100px', backgroundColor: '#FD5107', textTransform: 'none' }} variant="contained" color="success" onClick={onSaveClick} disabled={isConfirmed !== true}>Save</Button>
                            <Button style={{ margin: '10px', float: 'right', width: '100px' }} variant="contained" color="info" onClick={() => setIsConfirmModalVisible(false)}>Close</Button>
                        </div>
                    </Box>
                </Modal>
                <Modal
                    open={isAdminWarningVisible}
                    onClose={() => setIsAdminWarningVisible(false)}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description">
                    <Box sx={modalStyle}>
                        You need to have an admin account to access the data accessed via this link.<br /><br />
                        <Button style={{ margin: '10px', float: 'right', width: '100px' }} variant="contained" color="info" onClick={() => setIsAdminWarningVisible(false)}>Close</Button>
                    </Box>
                </Modal>
            </div>
            : '');
};
export default AppDetailsEntraInfo;